import HttpClient from "./httpClient";

/** One recording: the video of a single browser session within a run. */
export interface ComputerUseRecording {
  session_id: string;
  object_name: string;
  /**
   * A signed link to the video, valid for a short while. Handed out with the run so the
   * player can load itself as the thread opens, rather than showing a black rectangle until
   * somebody presses play.
   */
  video_url: string | null;
  /** A signed link to the poster frame. Not currently drawn; the video shows its own. */
  thumbnail_url: string | null;
  /**
   * The job that produced this video. Filled in by the client, not the server: one tool call can
   * span several jobs once a HITL pause is resumed under it, and a video has to be signed against
   * the job that actually banked it.
   */
  job_id?: number | null;
}

/** One browser run, as it can be rebuilt on a cold load. */
export interface ComputerUseRun {
  job_id: number;
  /** The tool call that opened the browser. Stable across a rescue's second session. */
  call_id: string;
  status: "in_queue" | "executing" | "waiting_on_customer" | "completed" | "error" | "cancelled" | "job_timeout";
  /** Only ever set while the run is live; a finished run's live URL is a dead page. */
  browser_url: string | null;
  started_at: string | null;
  /** One per session, in the order they happened. A rescued run has more than one. */
  recordings: ComputerUseRecording[];
}

class ComputerUseApi extends HttpClient {
  /** Every browser run on a thread, for rebuilding the inline panels without the live stream. */
  listRuns = (aiThreadId: number | string) =>
    this.getCoalesced(`/ai-threads/${aiThreadId}/computer-use`) as Promise<{ runs: ComputerUseRun[] }>;

  /**
   * A short-lived playable link for one session's recording.
   *
   * The run list already carries one for every recording it returns. This is for the two it
   * cannot cover: a recording that arrived on the live stream, which names the object without
   * being able to sign it, and one whose link expired while the thread sat open.
   */
  getRecordingUrl = (aiThreadId: number | string, jobId: number | string, sessionId: string) =>
    this.get(
      `/ai-threads/${aiThreadId}/computer-use/${jobId}/recording/${encodeURIComponent(sessionId)}`
    ) as Promise<{ url: string; expires_in_seconds: number }>;

  /**
   * A link to one session's poster frame.
   *
   * Wanted at a different moment than the video, which is why it is its own request. The run
   * list signs a poster for every recording it returns; this is for the one that just finished
   * in front of the technician, which reached them down the live stream naming its objects
   * without being able to sign either of them.
   */
  getPosterUrl = (aiThreadId: number | string, jobId: number | string, sessionId: string) =>
    this.get(
      `/ai-threads/${aiThreadId}/computer-use/${jobId}/poster/${encodeURIComponent(sessionId)}`
    ) as Promise<{ url: string; expires_in_seconds: number }>;
}

export default ComputerUseApi;
